import pick from 'lodash/pick';
import mapValues from 'lodash/mapValues';
import isObject from 'lodash/isObject';
import memoizeLast from '../../helpers/memoizeLast';
import calculateFrameRate from './calculate-frame-rate';
import { getBandwidth } from './congestionLevel/statsUtils';

function getFrameRateFromStats(stat) {
  return Number(stat.framesPerSecond || stat.framerateMean || stat.googFrameRateSent ||
    stat.googFrameRateReceived || stat.googFrameRateInput || stat.googFrameRateOutput || 0);
}

function getFrames(stat) {
  return Number(stat.framesEncoded || stat.framesDecoded);
}

function getLinkedTrack(stat, allStats) {
  if (!allStats) { return {}; }
  const linkedTrack = allStats.filter(({ id }) => id === stat.mediaTrackId);
  return linkedTrack.length ? linkedTrack[0] : {};
}

function totalPacketsLostReducer(totalPacketsLost, stat) {
  if ('packetsLost' in stat) {
    return totalPacketsLost + Math.max(stat.packetsLost, 0);
  }
  return totalPacketsLost;
}

const outboundStatNames = ['packetsSent', 'packetsLost', 'bytesSent'];
const getOutboundStats = stat => pick(stat, outboundStatNames);
const inboundStatNames = ['packetsReceived', 'packetsLost', 'bytesReceived'];
const getInboundStats = stat => pick(stat, inboundStatNames);
const outboundStatNamesWithFrames = outboundStatNames.concat(['framerateMean', 'googFrameRateSent', 'googFrameRateReceived', 'googFrameRateInput', 'googFrameRateOutput', 'framesEncoded', 'framesDecoded']);
const accumulateVideoStats = (videoArray, timestamp, id) => {
  const accumulatedVideo = {
    timestamp,
    id,
  };

  outboundStatNamesWithFrames.forEach((statName) => {
    accumulatedVideo[statName] = videoArray.reduce(
      (accumulator, stats) => accumulator + Number(stats[statName] || 0),
      0
    );
  });

  return accumulatedVideo;
};

function calcBitRate(bytesNow, bytesBefore, timeNow, timeBefore) {
  const timeDiff = (timeNow - timeBefore) / 1000; // convert to seconds
  const bytesDiff = bytesNow - bytesBefore;
  return timeDiff > 0 ? Math.round((bytesDiff * 8) / timeDiff) : 0; // in bps
}

const getStatsHelpers = () => {
  const prevRateTrackers = {};

  function getFrameRate(stat, startTime) {
    const frames = getFrames(stat);
    let frameRate = getFrameRateFromStats(stat);
    if (frameRate) {
      return { frameRate, frames };
    }

    if (isNaN(frames)) {
      return {};
    }

    const prevStatFrames = prevRateTrackers[stat.id] ? prevRateTrackers[stat.id].frames : 0;
    const prevTimestamp = prevRateTrackers[stat.id] ?
      prevRateTrackers[stat.id].timestamp : startTime;

    frameRate = calculateFrameRate({
      currentStatFrames: frames,
      currentTimestamp: stat.timestamp,
      prevStatFrames,
      prevTimestamp,
    });

    return { frameRate, frames };
  }

  function calcVideoRates(stat, startTime, isOutgoing) {
    const bytesKey = isOutgoing ? 'bytesSent' : 'bytesReceived';
    const headerBytesKey = isOutgoing ? 'headerBytesSent' : 'headerBytesReceived';
    const {
      timestamp: prevTimestamp,
      bytes: prevBytes,
      headerBytes: prevHeaderBytes,
      frameRate: prevFrameRate,
      bitrate: prevBitrate,
      totalBitrate: prevTotalBitrate,
    } =
      prevRateTrackers[stat.id] || { timestamp: startTime, bytes: 0, headerBytes: 0 };
    if (prevTimestamp === stat.timestamp) {
      return {
        frameRate: prevFrameRate,
        bitrate: prevBitrate,
        totalBitrate: prevTotalBitrate,
      };
    }
    const { frames, frameRate } = getFrameRate(stat, startTime);
    const bytes = Math.max(stat[bytesKey], prevBytes);
    const headerBytes = Math.max(stat[headerBytesKey], prevHeaderBytes);
    const currentBitrate = calcBitRate(bytes, prevBytes, stat.timestamp, prevTimestamp);
    const currentTotalBitrate = calcBitRate(
      bytes + headerBytes,
      prevBytes + prevHeaderBytes,
      stat.timestamp,
      prevTimestamp
    );
    let bitrate;
    let totalBitrate;
    if (prevBitrate === undefined && prevTotalBitrate === undefined) {
      bitrate = currentBitrate;
      totalBitrate = currentTotalBitrate;
    } else {
      const expWeight = 1 - Math.exp((prevTimestamp - stat.timestamp) / 2000);
      bitrate = Math.round((currentBitrate * expWeight) + (prevBitrate * (1 - expWeight)));
      totalBitrate =
        Math.round((currentTotalBitrate * expWeight) + (prevTotalBitrate * (1 - expWeight)));
    }
    prevRateTrackers[stat.id] = {
      ...prevRateTrackers[stat.id],
      bytes,
      headerBytes,
      timestamp: stat.timestamp,
      frames,
      frameRate,
      bitrate,
      totalBitrate,
    };

    return {
      frameRate,
      bitrate,
      totalBitrate,
    };
  }

  const api = {
    isVideoStat(stat, allStats) {
      // When kind is 'video', we need to check the type since there are other
      // stats with kind 'video' without the information we need.
      const isVideoKind = stat.kind === 'video' &&
        ['outbound-rtp', 'inbound-rtp'].includes(stat.type);

      return isVideoKind || stat.mediaType === 'video' ||
        'googFrameWidthReceived' in stat || // Old Chrome
        'googFrameWidthInput' in stat || // Old Chrome
        (stat.type === 'inbound-rtp' && stat.id.indexOf('Video') !== -1) || // Safari
        (stat.type === 'inboundrtp' && getLinkedTrack(stat, allStats).frameWidth !== 0); // Edge
    },
    isAudioStat(stat, allStats) {
      // When kind is 'audio', we need to check the type since there are other
      // stats with kind 'audio' without the information we need.
      const isAudioKind = stat.kind === 'audio' &&
        ['outbound-rtp', 'inbound-rtp'].includes(stat.type);

      return isAudioKind || stat.mediaType === 'audio' ||
        'audioInputLevel' in stat || // Old Chrome
        'audioOutputLevel' in stat || // Old Chrome
        (stat.type === 'inbound-rtp' && stat.id.indexOf('Audio') !== -1) || // Safari
        (stat.type === 'inboundrtp' && getLinkedTrack(stat, allStats).frameWidth === 0); // Edge;
    },
    isInboundStat(stat) {
      return 'bytesReceived' in stat || 'packetsReceived' in stat;
    },
    isOutboundStat(stat) {
      return 'bytesSent' in stat || 'packetsSent' in stat;
    },
    isVideoTrackStat(stat) {
      return stat.type === 'track' && (
        stat.frameHeight > 0 ||
        stat.frameWidth > 0 ||
        stat.framesCorrupted > 0 ||
        stat.framesDecoded > 0 ||
        stat.framesPerSecond > 0 ||
        stat.framesSent > 0 ||
        stat.framesReceived > 0
      );
    },
    isVideoRemoteInboundRtpStat(stat) {
      return stat.type === 'remote-inbound-rtp' && stat.kind === 'video';
    },
    isAudioRemoteInboundRtpStat(stat) {
      return stat.type === 'remote-inbound-rtp' && stat.kind === 'audio';
    },
    isVideoInboundRtpStat(stat) {
      return stat.type === 'inbound-rtp' && stat.kind === 'video';
    },
    parseStatCategory(stat, isVideoStat = api.isVideoStat(stat), stats) {
      const statCategory = {
        packetsLost: 0,
        packetsReceived: 0,
        bytesReceived: 0,
        // frameRate is only set for video stat
      };

      if ('packetsReceived' in stat) {
        statCategory.packetsReceived = Math.max(parseInt(stat.packetsReceived, 10), 0);
      }
      if ('packetsLost' in stat) {
        statCategory.packetsLost = Math.max(parseInt(stat.packetsLost, 10), 0);
      }
      if ('bytesReceived' in stat) {
        statCategory.bytesReceived = Math.max(parseInt(stat.bytesReceived, 10), 0);
      }

      if (isVideoStat) {
        statCategory.width = stat.frameWidth;
        statCategory.height = stat.frameHeight;
        if (stats) {
          const videoCodecs = stats.filter(codecStat => codecStat.type === 'codec' && codecStat.mimeType.includes('video'));
          const codec = videoCodecs.find(videoStat => videoStat.id === stat.codecId)?.mimeType.split('/')[1];
          if (codec) {
            statCategory.codec = codec;
          }
        }
        if (stat.pauseCount !== undefined) {
          statCategory.pauseCount = stat.pauseCount;
        }
        if (stat.freezeCount !== undefined) {
          statCategory.freezeCount = stat.freezeCount;
        }
        if (stat.totalPausesDuration !== undefined) {
          statCategory.totalPausesDuration = 1000 * stat.totalPausesDuration;
        }
        if (stat.totalFreezesDuration !== undefined) {
          statCategory.totalFreezesDuration = 1000 * stat.totalFreezesDuration;
        }
        const videoRates = calcVideoRates(stat, Date.now(), false);
        if (videoRates) {
          statCategory.frameRate = videoRates.frameRate;
          statCategory.decodedFrameRate = videoRates.frameRate;
          statCategory.bitrate = videoRates.bitrate;
          statCategory.totalBitrate = videoRates.totalBitrate;
        }
      }
      return statCategory;
    },
    getVideoPacketsLost(stats) {
      return stats
        .filter(stat => api.isVideoRemoteInboundRtpStat(stat, stats))
        .reduce(totalPacketsLostReducer, 0);
    },
    getAudioPacketsLost(stats) {
      return stats
        .filter(stat => api.isAudioRemoteInboundRtpStat(stat, stats))
        .reduce(totalPacketsLostReducer, 0);
    },
    normalizeTimestamp(timestamp, now = Date.now()) {
      if (isObject(timestamp) && 'getTime' in timestamp) {
        // Chrome as of 39 delivers a "kind of Date" object for timestamps
        // we duck check it and get the timestamp
        return timestamp.getTime();
      }

      if (Math.abs((timestamp / (1000 * now)) - 1) < 0.05) {
        // If the timestamp is within 5% of 1000x now, we assume its unit is microseconds and
        // divide by 1000 to correct for this.
        return timestamp / 1000;
      }

      return timestamp;
    },
    normalizeStats: memoizeLast((stats, inbound = true, startTime) => {
      const videos = stats.filter(stat =>
        api.isVideoStat(stat, stats) &&
        api.isInboundStat(stat) === inbound
      );
      const audio = stats.find(stat =>
        api.isAudioStat(stat, stats) &&
        api.isInboundStat(stat) === inbound
      );

      // There may be more than one video stream (e.g., when simulcast is enabled)
      // We just need to grab one of them for its id and timestamp
      const video = videos[0];

      if (!audio && !video) {
        throw new Error('could not find stats for either audio or video');
      }

      const timestamp = api.normalizeTimestamp(
        video ? video.timestamp : audio.timestamp);
      const otStats = {
        timestamp,
      };

      if (video && !inbound && video.packetsLost === undefined) {
        video.packetsLost = api.getVideoPacketsLost(stats);
      }

      if (audio && !inbound && audio.packetsLost === undefined) {
        audio.packetsLost = api.getAudioPacketsLost(stats);
      }

      const getCommonStats = inbound ? getInboundStats : getOutboundStats;

      if (videos && videos.length > 0) {
        const accumulatedVideo = accumulateVideoStats(videos, timestamp, `acc${video.id}`);
        const { frameRate } = getFrameRate(accumulatedVideo, startTime);

        otStats.video = Object.assign(
          { frameRate },
          mapValues(getCommonStats(accumulatedVideo), Number)
        );

        if (!inbound) {
          const videoCodecs = stats.filter(stat => stat.type === 'codec' && stat.mimeType.includes('video'));
          otStats.video.layers = videos
            .filter(videoStat => !api.isVideoRemoteInboundRtpStat(videoStat))
            .map((videoStat) => {
              const {
                frameRate: encodedFrameRate,
                bitrate,
                totalBitrate,
              } = calcVideoRates(videoStat, startTime, true);
              const layerStats = {
                width: videoStat.frameWidth,
                height: videoStat.frameHeight,
                codec: videoCodecs.find(codecStat => codecStat.id === videoStat.codecId)?.mimeType.split('/')[1],
                encodedFrameRate,
                bitrate,
                totalBitrate,
                scalabilityMode: videoStat.scalabilityMode,
              };
              if (videoStat.qualityLimitationReason && videoStat.qualityLimitationReason !== 'none') {
                layerStats.qualityLimitationReason = videoStat.qualityLimitationReason;
              }
              return layerStats;
            })
            .sort((a, b) => {
              // Sort by resolution (width * height) in descending order
              const resolutionA = (a.width || 0) * (a.height || 0);
              const resolutionB = (b.width || 0) * (b.height || 0);
              return resolutionB - resolutionA;
            });
          prevRateTrackers[`acc${video.id}`] = {
            ...prevRateTrackers[`acc${video.id}`],
            layers: otStats.video.layers.length,
          };
          otStats.mediaLink = {
            transport: {
              connectionEstimatedBandwidth: getBandwidth(stats),
            },
          };
        }
      }

      if (audio) {
        otStats.audio = mapValues(getCommonStats(audio), Number);
      }

      return otStats;
    }),
  };

  return api;
};

export default getStatsHelpers;
